import { getIssueDisplayName, getIssueVariantTip } from './rule-issue-tips';

const MAX_POPUP_TEXT_LENGTH = 70;

function getPopupLabels(ctx) {
	const hasI18n = ctx && typeof ctx.__ === 'function';

	return {
		rule: hasI18n ? ctx.__('Rule', 'a11yfix') : 'Rule',
		failed: hasI18n ? ctx.__('failed', 'a11yfix') : 'failed',
		rules: hasI18n ? ctx.__('Rules', 'a11yfix') : 'Rules',
		block: hasI18n ? ctx.__('Block', 'a11yfix') : 'Block',
		text: hasI18n ? ctx.__('Text', 'a11yfix') : 'Text',
		titleRuleProblem: hasI18n ? ctx.__('Rule name and problem', 'a11yfix') : 'Rule name and problem',
		titleTip: hasI18n ? ctx.__('Tip to fix', 'a11yfix') : 'Tip to fix',
		titleNote: hasI18n ? ctx.__('Note', 'a11yfix') : 'Note',
		noRuleDetails: hasI18n ? ctx.__('No rule details available.', 'a11yfix') : 'No rule details available.',
		unknown: hasI18n ? ctx.__('unknown', 'a11yfix') : 'unknown',
		genericTip: hasI18n
			? ctx.__('Review this element and update content/markup to meet accessibility rules.', 'a11yfix')
			: 'Review this element and update content/markup to meet accessibility rules.',
		defaultTip: hasI18n
			? ctx.__('Inspect element context and adjust content/markup for accessibility.', 'a11yfix')
			: 'Inspect element context and adjust content/markup for accessibility.',
		multiRuleNote: hasI18n
			? ctx.__('Multiple rule issues are possible for this element. Toggle one rule button to display the exact problem.', 'a11yfix')
			: 'Multiple rule issues are possible for this element. Toggle one rule button to display the exact problem.'
	};
}

function getRulePopupMetaMap(ctx) {
	const hasI18n = ctx && typeof ctx.__ === 'function';

	return {
		linkTitle: {
			name: hasI18n ? ctx.__('Link text', 'a11yfix') : 'Link text',
			tip: hasI18n
				? ctx.__('Ensure each link has descriptive visible text.', 'a11yfix')
				: 'Ensure each link has descriptive visible text.'
		},
		headingOrder: {
			name: hasI18n ? ctx.__('Heading order', 'a11yfix') : 'Heading order',
			tip: hasI18n
				? ctx.__('Keep heading levels in a logical hierarchy without skipping levels.', 'a11yfix')
				: 'Keep heading levels in a logical hierarchy without skipping levels.'
		},
		imageAlt: {
			name: hasI18n ? ctx.__('Image alt text', 'a11yfix') : 'Image alt text',
			tip: hasI18n
				? ctx.__('Add concise alt text that describes the image purpose.', 'a11yfix')
				: 'Add concise alt text that describes the image purpose.'
		},
		formLabels: {
			name: hasI18n ? ctx.__('Form labels', 'a11yfix') : 'Form labels',
			tip: hasI18n
				? ctx.__('Associate each input with a clear visible label.', 'a11yfix')
				: 'Associate each input with a clear visible label.'
		},
		keyboardFocusable: {
			name: hasI18n ? ctx.__('Keyboard focus', 'a11yfix') : 'Keyboard focus',
			tip: hasI18n
				? ctx.__('Ensure interactive elements are keyboard-focusable and usable.', 'a11yfix')
				: 'Ensure interactive elements are keyboard-focusable and usable.'
		},
		accessibleNamePreferredSource: {
			name: hasI18n ? ctx.__('Accessible name source', 'a11yfix') : 'Accessible name source',
			tip: hasI18n
				? ctx.__('Avoid aria-label text that conflicts with visible labels or text.', 'a11yfix')
				: 'Avoid aria-label text that conflicts with visible labels or text.'
		},
		ariaLabelledbyIntegrity: {
			name: hasI18n ? ctx.__('aria-labelledby integrity', 'a11yfix') : 'aria-labelledby integrity',
			tip: hasI18n
				? ctx.__('Ensure aria-labelledby IDs exist and resolve to readable text.', 'a11yfix')
				: 'Ensure aria-labelledby IDs exist and resolve to readable text.'
		},
		ariaHiddenInteractive: {
			name: hasI18n ? ctx.__('aria-hidden interactive', 'a11yfix') : 'aria-hidden interactive',
			tip: hasI18n
				? ctx.__('Do not hide interactive content with aria-hidden="true".', 'a11yfix')
				: 'Do not hide interactive content with aria-hidden="true".'
		},
		lowColorContrast: {
			name: hasI18n ? ctx.__('Color contrast', 'a11yfix') : 'Color contrast',
			tip: hasI18n
				? ctx.__('Increase contrast between text and background to meet WCAG ratios.', 'a11yfix')
				: 'Increase contrast between text and background to meet WCAG ratios.'
		}
	};
}

function truncatePopupText(value, maxLength = MAX_POPUP_TEXT_LENGTH) {
	const normalized = String(value || '').replace(/\s+/g, ' ').trim();
	if (!normalized) {
		return '';
	}

	if (!Number.isInteger(maxLength) || maxLength <= 0 || normalized.length <= maxLength) {
		return normalized;
	}

	return `${normalized.slice(0, maxLength - 1)}...`;
}

function startCase(value) {
	const raw = String(value || '').trim();
	if (!raw) {
		return 'Rule';
	}

	return raw
		.replace(/([a-z])([A-Z])/g, '$1 $2')
		.replace(/[-_]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/^./, (char) => char.toUpperCase());
}

function startCaseWithContext(ctx, value) {
	const result = startCase(value);
	if (result !== 'Rule') {
		return result;
	}

	return ctx && typeof ctx.__ === 'function'
		? ctx.__('Rule', 'a11yfix')
		: 'Rule';
}

function getRulePopupMeta(ctx, ruleKey) {
	const predefined = getRulePopupMetaMap(ctx)[ruleKey] || null;
	const labels = getPopupLabels(ctx);

	if (predefined) {
		return predefined;
	}

	return {
		name: startCaseWithContext(ctx, ruleKey),
		tip: labels.genericTip
	};
}

function resolveRuleKeysForPopup(ctx, failedRuleKeys) {
	const allKeys = ctx.normalizeRuleKeys(failedRuleKeys);
	const labels = getPopupLabels(ctx);

	if (allKeys.length <= 1) {
		return {
			selectedRuleKeys: allKeys,
			note: ''
		};
	}

	const activeRuleSet = ctx?.activeRuleHighlightKeys instanceof Set
		? ctx.activeRuleHighlightKeys
		: new Set();
	const activeMatched = allKeys.filter((ruleKey) => activeRuleSet.has(ruleKey));

	if (activeMatched.length === 1) {
		return {
			selectedRuleKeys: activeMatched,
			note: ''
		};
	}

	if (activeMatched.length > 1) {
		return {
			selectedRuleKeys: [activeMatched[0]],
			note: labels.multiRuleNote
		};
	}

	return {
		selectedRuleKeys: [allKeys[0]],
		note: ''
	};
}

function resolveFirstIssueText(ctx, ruleKey, liveBlock = {}) {
	const details = ctx.rulesViewRuleMetadataByKey?.[ruleKey]?.details;
	const activeNode = liveBlock?.node || null;
	const activeSelector = String(liveBlock?.selector || '').trim();
	const normalizedText = ctx.normalizeComparableText(liveBlock?.textPreview || '');

	if (!details || typeof details !== 'object') {
		return '';
	}

	const matchesCurrentTarget = (item) => {
		if (!item || typeof item !== 'object') {
			return false;
		}

		const selector = String(item.selector || '').trim();

		if (activeSelector && selector && activeSelector === selector) {
			return true;
		}

		if (!activeNode || !selector || typeof activeNode.matches !== 'function') {
			return false;
		}

		try {
			return activeNode.matches(selector);
		} catch {
			return false;
		}
	};

	const pickByText = (items, projector) => {
		if (!Array.isArray(items) || items.length === 0) {
			return null;
		}

		if (!normalizedText) {
			return items[0] || null;
		}

		let bestItem = null;
		let bestScore = -1;

		for (const item of items) {
			const candidateText = ctx.normalizeComparableText(projector(item));

			if (!candidateText) {
				continue;
			}

			let score = 0;

			if (candidateText === normalizedText) {
				score = 4;
			} else if (candidateText.includes(normalizedText) || normalizedText.includes(candidateText)) {
				score = 3;
			} else if (ctx.hasStrongTokenOverlap(normalizedText, candidateText, 2)) {
				score = 2;
			}

			if (score > bestScore) {
				bestScore = score;
				bestItem = item;
			}
		}

		return bestItem || items[0] || null;
	};

	// headingOrder: details.issues is a string array
	if (ruleKey === 'headingOrder') {
		const failedItems = Array.isArray(details.failedItems) ? details.failedItems : [];
		const matchedFailedItem = failedItems.find((item) => matchesCurrentTarget(item));

		if (matchedFailedItem?.type === 'outOfOrderSequence') {
			const headingTag = String(matchedFailedItem.tag || '').toUpperCase();
			return headingTag
				? `Out-of-order sequence near ${headingTag}`
				: 'Out-of-order sequence near heading';
		}

		if (matchedFailedItem?.type === 'firstHeadingNotH1') {
			return 'Out-of-order: first heading is not H1.';
		}

		if (matchedFailedItem?.type === 'skippedLevel') {
			const issues = Array.isArray(details.issues) ? details.issues : [];
			const skippedIssue = issues.find((item) => typeof item === 'string' && item.startsWith('Skipped level'));
			return skippedIssue || 'Skipped level';
		}

		if (matchedFailedItem?.type === 'multipleH1') {
			const issues = Array.isArray(details.issues) ? details.issues : [];
			const multipleH1Issue = issues.find((item) => typeof item === 'string' && item.startsWith('Multiple H1'));
			return multipleH1Issue || 'Multiple H1 headings found';
		}

		if (matchedFailedItem?.type === 'presentationalHeading') {
			const issues = Array.isArray(details.issues) ? details.issues : [];
			const presentationalIssue = issues.find((item) => typeof item === 'string' && item.startsWith('Presentational headings found'));
			return presentationalIssue || 'Presentational headings found';
		}

		const issues = Array.isArray(details.issues) ? details.issues : [];
		const firstString = pickByText(issues, (item) => item) || issues.find((item) => typeof item === 'string' && item.length > 0);
		return firstString || '';
	}

	if (ruleKey === 'lowColorContrast') {
		const checks = Array.isArray(details.checks)
			? details.checks.filter((check) => check?.isFail === true)
			: [];
		const matchedCheck = checks.find((check) => matchesCurrentTarget(check));

		if (matchedCheck) {
			return matchedCheck?.hasImageIntersection === true ? 'image-intersection' : '';
		}
		const hasImageIntersection = checks.some((check) => check?.hasImageIntersection === true);

		return hasImageIntersection ? 'image-intersection' : '';
	}

	// All other rules: look for .issue string on items
	const selectorMatchedIssues = [
		...(Array.isArray(details.issues) ? details.issues : []),
		...(Array.isArray(details.elements) ? details.elements : []),
		...(Array.isArray(details.invalidControls) ? details.invalidControls : []),
		...(Array.isArray(details.invalidLinks) ? details.invalidLinks : []),
		...(Array.isArray(details.invalidImages) ? details.invalidImages : []),
		...(Array.isArray(details.checks) ? details.checks : [])
	].filter((item) => item && typeof item === 'object' && typeof item.issue === 'string' && item.issue.length > 0);

	const selectorMatchedIssue = selectorMatchedIssues.find((item) => matchesCurrentTarget(item));

	if (selectorMatchedIssue) {
		return selectorMatchedIssue.issue;
	}

	const candidates = [
		...(Array.isArray(details.issues) ? details.issues : []),
		...(Array.isArray(details.elements) ? details.elements : []),
		...(Array.isArray(details.invalidControls) ? details.invalidControls : []),
	];

	const found = pickByText(
		candidates,
		(item) => item?.issue || item?.text || item?.visibleNameValue || item?.ariaLabel || item?.labelValue || item?.title || ''
	);

	if (found && typeof found?.issue === 'string' && found.issue.length > 0) {
		return found.issue;
	}

	const fallbackFound = candidates.find((item) => typeof item?.issue === 'string' && item.issue.length > 0);

	if (fallbackFound) {
		return fallbackFound.issue;
	}

	if (ruleKey === 'imageAlt' || ruleKey === 'linkTitle') {
		const snippet = buildRuleSnippetForHover(ctx, ruleKey, liveBlock);
		return typeof snippet === 'string' ? snippet : '';
	}

	return '';
}

function buildPopupPayload(ctx, options = {}) {
	const failedRuleKeys = ctx.normalizeRuleKeys(options.failedRuleKeys);
	const ruleSelection = resolveRuleKeysForPopup(ctx, failedRuleKeys);
	const blockType = String(options.blockType || options.liveBlock?.canonicalBlockType || options.liveBlock?.blockType || 'unknown').trim();
	const textPreview = truncatePopupText(options.textPreview || options.liveBlock?.textPreview || '');
	const resolvedLiveBlock = {
		...(options.liveBlock || {}),
		textPreview,
		blockType
	};
	const rules = ruleSelection.selectedRuleKeys.map((ruleKey) => {
		const meta = getRulePopupMeta(ctx, ruleKey);
		const problem = buildRuleSnippetForHover(ctx, ruleKey, resolvedLiveBlock);
		const issueText = resolveFirstIssueText(ctx, ruleKey, resolvedLiveBlock);
		const variantTip = getIssueVariantTip(ctx.__, ruleKey, issueText);

		return {
			key: ruleKey,
			name: getIssueDisplayName(ctx.__, issueText, meta.name),
			problem,
			tip: variantTip || meta.tip
		};
	});

	return {
		rules,
		block: blockType || 'unknown',
		text: textPreview,
		note: ruleSelection.note || ''
	};
}

function popupPayloadToLegacyText(ctx, payload) {
	if (!payload || typeof payload !== 'object') {
		return '';
	}

	const labels = getPopupLabels(ctx);
	const rulesText = (Array.isArray(payload.rules) ? payload.rules : [])
		.map((rule) => `${rule.name || labels.rule}: ${rule.problem || labels.failed}`)
		.join(' | ');
	const blockText = payload.block ? ` | ${labels.block}: ${payload.block}` : '';
	const textValue = payload.text ? ` | ${labels.text}: ${payload.text}` : '';

	if (!rulesText && !blockText && !textValue) {
		return '';
	}

	return `${labels.rules}: ${rulesText}${blockText}${textValue}`.slice(0, 900);
}

export function buildLiveEditorMatchHoverText(ctx, liveBlock) {
	if (!liveBlock || typeof liveBlock !== 'object') {
		return '';
	}

	const payload = buildPopupPayload(ctx, {
		failedRuleKeys: liveBlock.failedRuleKeys,
		blockType: liveBlock.canonicalBlockType || liveBlock.blockType,
		textPreview: liveBlock.textPreview,
		liveBlock
	});

	return popupPayloadToLegacyText(ctx, payload).slice(0, 700);
}

export function buildRuleSnippetForHover(ctx, ruleKey, liveBlock) {
	const details = ctx.rulesViewRuleMetadataByKey?.[ruleKey]?.details;
	const labels = getPopupLabels(ctx);
	const t = typeof ctx?.__ === 'function' ? ctx.__ : (text) => text;

	if (!details || typeof details !== 'object') {
		return labels.failed;
	}

	const textPreview = String(liveBlock?.textPreview || '').trim();
	const normalizedText = ctx.normalizeComparableText(textPreview);
	const compact = (value, fallback = labels.failed) => {
		const result = String(value || '').replace(/\s+/g, ' ').trim();
		if (!result) {
			return fallback;
		}

		return result.slice(0, 120);
	};

	const pickByText = (items, projector) => {
		if (!Array.isArray(items) || items.length === 0) {
			return null;
		}

		if (!normalizedText) {
			return items[0];
		}

		let bestItem = null;
		let bestScore = -1;

		for (const item of items) {
			const candidateText = ctx.normalizeComparableText(projector(item));

			if (!candidateText) {
				continue;
			}

			let score = 0;

			if (candidateText === normalizedText) {
				score = 4;
			} else if (candidateText.includes(normalizedText) || normalizedText.includes(candidateText)) {
				score = 3;
			} else if (ctx.hasStrongTokenOverlap(normalizedText, candidateText, 2)) {
				score = 2;
			}

			if (score > bestScore) {
				bestScore = score;
				bestItem = item;
			}
		}

		return bestItem || items[0];
	};

	if (ruleKey === 'linkTitle') {
		const item = pickByText(details.invalidLinks || details.links || [], (candidate) => candidate?.text || candidate?.title || candidate?.href || '');
		if (!item) {
			return t('Link text issue', 'a11yfix');
		}

		return compact(`${item.text ? `text=${item.text}` : t('missing link text', 'a11yfix')} ${item.href ? `href=${item.href}` : ''}`);
	}

	if (ruleKey === 'imageAlt') {
		const item = pickByText(details.invalidImages || details.images || [], (candidate) => candidate?.alt || candidate?.src || '');
		if (!item) {
			return t('Image alt issue', 'a11yfix');
		}

		return compact(`${item.alt ? `alt=${item.alt}` : t('missing alt', 'a11yfix')} ${item.src ? `src=${item.src}` : ''}`);
	}

	if (ruleKey === 'headingOrder') {
		const failedItem = pickByText(details.failedItems || [], (candidate) => candidate?.text || candidate?.tag || '');
		if (failedItem) {
			return compact(`${failedItem.tag || t('heading', 'a11yfix')} ${failedItem.text || ''}`);
		}

		const issueText = Array.isArray(details.issues) ? details.issues[0] : '';
		return compact(issueText, t('Heading order issue', 'a11yfix'));
	}

	if (ruleKey === 'formLabels') {
		const item = pickByText(details.invalidControls || details.controls || [], (candidate) => candidate?.issue || candidate?.name || candidate?.id || '');
		if (!item) {
			return t('Form label issue', 'a11yfix');
		}

		return compact(item.issue || `${item.tag || t('control', 'a11yfix')} ${item.name || item.id || ''}`);
	}

	if (ruleKey === 'keyboardFocusable') {
		const item = pickByText(details.issues || details.elements || [], (candidate) => candidate?.issue || candidate?.tag || candidate?.text || '');
		if (!item) {
			return t('Keyboard focus issue', 'a11yfix');
		}

		return compact(item.issue || `${item.tag || t('element', 'a11yfix')} ${t('focus issue', 'a11yfix')}`);
	}

	if (ruleKey === 'accessibleNamePreferredSource') {
		const item = pickByText(details.issues || details.elements || [], (candidate) => candidate?.issue || candidate?.visibleNameValue || candidate?.ariaLabel || '');
		if (!item) {
			return t('Accessible name source issue', 'a11yfix');
		}

		return compact(item.issue || `visible=${item.visibleNameValue || '-'} aria-label=${item.ariaLabel || '-'}`);
	}

	if (ruleKey === 'ariaLabelledbyIntegrity') {
		const item = pickByText(details.issues || details.elements || [], (candidate) => candidate?.issue || candidate?.ariaLabelledby || candidate?.id || '');
		if (!item) {
			return t('aria-labelledby issue', 'a11yfix');
		}

		return compact(item.issue || `aria-labelledby=${item.ariaLabelledby || '-'}`);
	}

	if (ruleKey === 'ariaHiddenInteractive') {
		const item = pickByText(details.issues || details.elements || [], (candidate) => candidate?.issue || candidate?.tag || candidate?.role || '');
		if (!item) {
			return t('aria-hidden interactive issue', 'a11yfix');
		}

		return compact(item.issue || `${item.tag || t('element', 'a11yfix')} ${t('has aria-hidden issue', 'a11yfix')}`);
	}

	if (ruleKey === 'lowColorContrast') {
		const checks = Array.isArray(details.checks)
			? details.checks.filter((check) => check?.isFail === true)
			: [];
		const item = pickByText(details.issues || checks, (candidate) => candidate?.text || candidate?.selector || '');
		if (!item) {
			return t('Contrast issue', 'a11yfix');
		}

		if (item?.hasImageIntersection === true) {
			return 'image-intersection';
		}

		const ratio = item.contrastRatio ? `ratio=${item.contrastRatio}` : '';
		return compact(`${ratio} ${item.text || item.selector || t('contrast issue', 'a11yfix')}`);
	}

	const genericArrays = [
		details.invalidLinks,
		details.invalidImages,
		details.invalidControls,
		details.failedItems,
		details.issues,
		details.checks
	].find((value) => Array.isArray(value) && value.length > 0);

	if (genericArrays) {
		const item = pickByText(genericArrays, (candidate) => candidate?.text || candidate?.issue || candidate?.title || candidate?.selector || '');
		return compact(item?.issue || item?.text || item?.title || item?.selector || labels.failed);
	}

	return labels.failed;
}

export function resolveBlockTypeFromNode(node) {
	if (!node || !node.classList) {
		return node?.tagName?.toLowerCase?.() || 'unknown';
	}

	const blockClassName = Array.from(node.classList).find((className) => className.startsWith('wp-block-'));
	if (blockClassName) {
		return blockClassName.replace(/^wp-block-/, '').trim() || 'unknown';
	}

	return node.tagName?.toLowerCase?.() || 'unknown';
}

export function appendRuleHoverDataForNode(ctx, node, ruleKey) {
	if (!node || node.nodeType !== 1 || typeof node.getAttribute !== 'function' || !ruleKey) {
		return;
	}

	const existingKeys = String(node.getAttribute('data-a11yfix-rule-keys') || '')
		.split(',')
		.map((value) => value.trim())
		.filter((value) => value.length > 0);
	const mergedKeys = Array.from(new Set([...existingKeys, ruleKey]));
	const textPreview = truncatePopupText(node.textContent || '', MAX_POPUP_TEXT_LENGTH);
	const blockType = resolveBlockTypeFromNode(node);
	const payload = buildPopupPayload(ctx, {
		failedRuleKeys: mergedKeys,
		blockType,
		textPreview,
		liveBlock: {
			node,
			textPreview,
			blockType
		}
	});

	node.setAttribute('data-a11yfix-rule-keys', mergedKeys.join(','));
	node.setAttribute('data-a11yfix-rule-popup-json', JSON.stringify(payload));
	node.setAttribute('data-a11yfix-rule-popup', popupPayloadToLegacyText(ctx, payload));
}

export function setLiveEditorMatchHoverData(ctx, node, liveBlock) {
	if (!node || node.nodeType !== 1 || typeof node.setAttribute !== 'function') {
		return;
	}

	const failedRuleKeys = ctx.normalizeRuleKeys(liveBlock?.failedRuleKeys);
	const payload = buildPopupPayload(ctx, {
		failedRuleKeys,
		blockType: liveBlock?.canonicalBlockType || liveBlock?.blockType,
		textPreview: liveBlock?.textPreview,
		liveBlock
	});
	const hoverText = popupPayloadToLegacyText(ctx, payload);

	if (!hoverText && (!Array.isArray(payload.rules) || payload.rules.length === 0)) {
		return;
	}

	node.setAttribute('data-a11yfix-rule-popup-json', JSON.stringify(payload));
	node.setAttribute('data-a11yfix-rule-popup', hoverText);

	if (failedRuleKeys.length > 0) {
		node.setAttribute('data-a11yfix-rule-keys', failedRuleKeys.join(','));
	}
}

export function ensureLiveEditorHoverPopupBinding(ctx, documentNode) {
	if (!documentNode || ctx.liveEditorPopupBindings.has(documentNode)) {
		return;
	}

	const resolvePopupContext = (node) => {
		if (!node || node.nodeType !== 1 || typeof node.closest !== 'function') {
			return {
				text: '',
				node: null
			};
		}

		const matchedNode = node.closest('[data-a11yfix-rule-popup]');
		let payload = null;

		try {
			const payloadRaw = matchedNode?.getAttribute('data-a11yfix-rule-popup-json') || '';
			payload = payloadRaw ? JSON.parse(payloadRaw) : null;
		} catch {
			payload = null;
		}

		return {
			text: matchedNode?.getAttribute('data-a11yfix-rule-popup') || '',
			payload,
			node: matchedNode || null
		};
	};

	const onMouseOver = (event) => {
		const popupContext = resolvePopupContext(event.target);
		const popupText = popupContext.text;
		if (!popupText) {
			return;
		}

		showLiveEditorHoverPopup(ctx, documentNode, popupText, {
			x: event.clientX,
			y: event.clientY,
			payload: popupContext.payload,
			node: popupContext.node
		});
	};

	const onMouseMove = (event) => {
		const popupContext = resolvePopupContext(event.target);
		const popupText = popupContext.text;
		if (!popupText) {
			return;
		}

		showLiveEditorHoverPopup(ctx, documentNode, popupText, {
			x: event.clientX,
			y: event.clientY,
			payload: popupContext.payload,
			node: popupContext.node
		});
	};

	const onMouseOut = (event) => {
		const popupText = resolvePopupContext(event.relatedTarget).text;
		if (popupText) {
			showLiveEditorHoverPopup(ctx, documentNode, popupText, {
				x: event.clientX,
				y: event.clientY,
				payload: resolvePopupContext(event.relatedTarget).payload
			});
			return;
		}

		hideLiveEditorHoverPopup(documentNode);
	};

	const onFocusIn = (event) => {
		const popupContext = resolvePopupContext(event.target);
		const popupText = popupContext.text;
		if (!popupText) {
			return;
		}

		showLiveEditorHoverPopup(ctx, documentNode, popupText, {
			payload: popupContext.payload,
			node: popupContext.node
		});
	};

	const onFocusOut = (event) => {
		const popupContext = resolvePopupContext(event.relatedTarget);
		const popupText = popupContext.text;
		if (popupText) {
			showLiveEditorHoverPopup(ctx, documentNode, popupText, {
				payload: popupContext.payload,
				node: popupContext.node
			});
			return;
		}

		hideLiveEditorHoverPopup(documentNode);
	};

	documentNode.addEventListener('mouseover', onMouseOver);
	documentNode.addEventListener('mousemove', onMouseMove);
	documentNode.addEventListener('mouseout', onMouseOut);
	documentNode.addEventListener('focusin', onFocusIn);
	documentNode.addEventListener('focusout', onFocusOut);

	ctx.liveEditorPopupBindings.set(documentNode, {
		onMouseOver,
		onMouseMove,
		onMouseOut,
		onFocusIn,
		onFocusOut
	});
}

export function teardownLiveEditorHoverPopupBinding(ctx, documentNode) {
	if (!documentNode) {
		return;
	}

	const binding = ctx.liveEditorPopupBindings.get(documentNode);

	if (binding) {
		documentNode.removeEventListener('mouseover', binding.onMouseOver);
		documentNode.removeEventListener('mousemove', binding.onMouseMove);
		documentNode.removeEventListener('mouseout', binding.onMouseOut);
		documentNode.removeEventListener('focusin', binding.onFocusIn);
		documentNode.removeEventListener('focusout', binding.onFocusOut);
		ctx.liveEditorPopupBindings.delete(documentNode);
	}

	const popupNode = documentNode.getElementById('a11yfix-live-editor-hover-popup');
	if (popupNode) {
		popupNode.remove();
	}
}

export function ensureLiveEditorHoverPopupNode(documentNode) {
	if (!documentNode) {
		return null;
	}

	let popupNode = documentNode.getElementById('a11yfix-live-editor-hover-popup');

	if (!popupNode) {
		popupNode = documentNode.createElement('aside');
		popupNode.id = 'a11yfix-live-editor-hover-popup';
		popupNode.setAttribute('role', 'status');
		popupNode.setAttribute('aria-live', 'polite');
		popupNode.style.position = 'fixed';
		popupNode.style.top = '12px';
		popupNode.style.right = '12px';
		popupNode.style.maxWidth = '360px';
		popupNode.style.maxHeight = '45vh';
		popupNode.style.overflowY = 'auto';
		popupNode.style.padding = '10px 12px';
		popupNode.style.borderRadius = '8px';
		popupNode.style.border = '1px solid rgba(15, 23, 42, 0.18)';
		popupNode.style.background = '#ffffff';
		popupNode.style.color = '#0f172a';
		popupNode.style.fontSize = '12px';
		popupNode.style.lineHeight = '1.5';
		popupNode.style.boxShadow = '0 8px 24px rgba(15, 23, 42, 0.18)';
		popupNode.style.zIndex = '2147483647';
		popupNode.style.pointerEvents = 'none';
		popupNode.style.whiteSpace = 'normal';
		popupNode.style.display = 'none';
		documentNode.body?.appendChild(popupNode);
	}

	return popupNode;
}

function escapePopupHtml(value) {
	return String(value || '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

function parsePopupTextSections(rawText) {
	const text = String(rawText || '').trim();
	const pattern = /^Rules:\s*([\s\S]*?)\s+\|\s+Block:\s*([^|]*?)(?:\s+\|\s+Text:\s*([\s\S]*))?$/;
	const matches = text.match(pattern);

	if (!matches) {
		return {
			rules: [],
			block: '',
			text: text
		};
	}

	const rulesPart = String(matches[1] || '').trim();
	const blockPart = String(matches[2] || '').trim();
	const textPart = String(matches[3] || '').trim();

	const rules = rulesPart
		.split(/\s+\|\s+/)
		.map((entry) => entry.trim())
		.filter((entry) => entry.length > 0);

	return {
		rules,
		block: blockPart,
		text: textPart
	};
}

function normalizePopupPayload(ctx, popupContent, directPayload = null) {

	if (directPayload && typeof directPayload === 'object') {
		const labels = getPopupLabels(ctx);
		const rules = Array.isArray(directPayload.rules)
			? directPayload.rules.map((rule) => ({
				name: String(rule?.name || labels.rule).trim(),
				problem: String(rule?.problem || labels.failed).trim(),
				tip: String(rule?.tip || '').trim()
			}))
			: [];

		return {
			rules,
			block: String(directPayload.block || '').trim(),
			text: truncatePopupText(directPayload.text || '', MAX_POPUP_TEXT_LENGTH),
			note: String(directPayload.note || '').trim() || ''
		};
	}

	const parsed = parsePopupTextSections(String(popupContent || ''));
	if (!parsed.block && !parsed.text && parsed.rules.length === 0) {
		return null;
	}

	const labels = getPopupLabels(ctx);
	return {
		rules: parsed.rules.map((entry) => ({
			name: labels.rule,
			problem: entry,
			tip: ''
		})),
		block: parsed.block,
		text: truncatePopupText(parsed.text || '', MAX_POPUP_TEXT_LENGTH),
		note: ''
	};
}

function buildPopupHtml(ctx, rawText, directPayload = null) {
	const payload = normalizePopupPayload(ctx, rawText, directPayload);

	if (!payload) {
		return '';
	}

	const rules = Array.isArray(payload.rules) ? payload.rules : [];
	const labels = getPopupLabels(ctx);
	const note = String(payload?.note || '').trim();
	const tips = Array.from(new Set(rules.map((rule) => String(rule?.tip || '').trim()).filter((tip) => tip.length > 0)));
	const rulesRows = rules.map((rule) => {
		const ruleName = escapePopupHtml(rule?.name || labels.rule);
		const problem = escapePopupHtml(rule?.problem || labels.failed);
		return `<li style="margin:0 0 6px 0;line-height:1.4;"><span style="display:inline-block;padding:1px 6px;border-radius:999px;background:#e2e8f0;color:#0f172a;font-weight:600;margin-right:6px;">${ruleName}</span><span style="color:#1e293b;">${problem}</span></li>`;
	});

	const tipsRows = tips.map((tip) => `<li style="margin:0 0 4px 0;line-height:1.4;color:#1e293b;">${escapePopupHtml(tip)}</li>`);

	const rulesHeading = escapePopupHtml(labels.titleRuleProblem);
	const noRuleDetailsText = escapePopupHtml(labels.noRuleDetails);
	const blockHeading = escapePopupHtml(labels.block);
	const unknownText = escapePopupHtml(labels.unknown);
	const textHeading = escapePopupHtml(labels.text);
	const tipsHeading = escapePopupHtml(labels.titleTip);
	const defaultTipText = escapePopupHtml(labels.defaultTip);

	const rulesHtml = `<div style="margin:0;"><div style="font-weight:700;color:#0f172a;margin:0 0 4px 0;">${rulesHeading}</div><ul style="margin:0;padding:0 0 0 4px;list-style:none;">${rulesRows.join('') || `<li style="margin:0;line-height:1.4;color:#334155;">${noRuleDetailsText}</li>`}</ul></div>`;
	const blockHtml = `<div style="margin:8px 0 0 0;"><span style="font-weight:700;color:#0f172a;">${blockHeading}</span><span style="margin-left:8px;color:#1e293b;">${escapePopupHtml(payload.block || '') || unknownText}</span></div>`;
	const textHtml = `<div style="margin:8px 0 0 0;"><span style="font-weight:700;color:#0f172a;">${textHeading}</span><div style="margin-top:4px;color:#1e293b;line-height:1.45;">${escapePopupHtml(payload.text || '')}</div></div>`;
	const tipsHtml = `<div style="margin:8px 0 0 0;"><div style="font-weight:700;color:#0f172a;margin:0 0 4px 0;">${tipsHeading}</div><ul style="margin:0;padding:0 0 0 16px;">${tipsRows.join('') || `<li style="margin:0;line-height:1.4;color:#334155;">${defaultTipText}</li>`}</ul></div>`;
 	const noteHtml = note
		? `<div style="margin:8px 0 0 0;padding:6px 8px;border-radius:6px;background:#fff7ed;border:1px solid #fed7aa;"><span style="font-weight:700;color:#9a3412;">${escapePopupHtml(labels.titleNote)}:</span><span style="margin-left:6px;color:#7c2d12;">${escapePopupHtml(note)}</span></div>`
		: '';

	return `<div>${rulesHtml}${blockHtml}${textHtml}${tipsHtml}${noteHtml}</div>`;
}

function resolvePopupAnchorPoint(documentNode, positionHint = {}) {
	const viewportWidth = documentNode?.documentElement?.clientWidth || documentNode?.defaultView?.innerWidth || 0;
	const viewportHeight = documentNode?.documentElement?.clientHeight || documentNode?.defaultView?.innerHeight || 0;

	const hasPointer = Number.isFinite(positionHint?.x) && Number.isFinite(positionHint?.y);
	if (hasPointer) {
		return {
			x: Number(positionHint.x),
			y: Number(positionHint.y),
			viewportWidth,
			viewportHeight
		};
	}

	const anchorNode = positionHint?.node;
	if (anchorNode && typeof anchorNode.getBoundingClientRect === 'function') {
		const rect = anchorNode.getBoundingClientRect();
		return {
			x: Math.round(rect.left + rect.width / 2),
			y: Math.round(rect.top + rect.height / 2),
			viewportWidth,
			viewportHeight
		};
	}

	return {
		x: Math.round(viewportWidth / 2),
		y: Math.round(viewportHeight / 2),
		viewportWidth,
		viewportHeight
	};
}

function applyLiveEditorHoverPopupPosition(popupNode, documentNode, positionHint = {}) {
	if (!popupNode || !documentNode) {
		return;
	}

	const gapPx = 12;
	const anchor = resolvePopupAnchorPoint(documentNode, positionHint);
	const viewportWidth = Math.max(0, Number(anchor.viewportWidth) || 0);
	const viewportHeight = Math.max(0, Number(anchor.viewportHeight) || 0);
	const isLeftHalf = anchor.x <= viewportWidth / 2;
	const isTopHalf = anchor.y <= viewportHeight / 2;

	popupNode.style.top = 'auto';
	popupNode.style.bottom = 'auto';
	popupNode.style.left = 'auto';
	popupNode.style.right = 'auto';

	if (isTopHalf) {
		popupNode.style.top = `${Math.max(gapPx, anchor.y + gapPx)}px`;
	} else {
		popupNode.style.bottom = `${Math.max(gapPx, viewportHeight - anchor.y + gapPx)}px`;
	}

	if (isLeftHalf) {
		popupNode.style.left = `${Math.max(gapPx, anchor.x + gapPx)}px`;
	} else {
		popupNode.style.right = `${Math.max(gapPx, viewportWidth - anchor.x + gapPx)}px`;
	}

	const verticalPlacement = isTopHalf ? 'top' : 'bottom';
	const horizontalPlacement = isLeftHalf ? 'left' : 'right';
	popupNode.setAttribute('data-a11yfix-placement', `${verticalPlacement}-${horizontalPlacement}`);
}

export function showLiveEditorHoverPopup(ctx, documentNode, popupText, positionHint = {}) {
	const popupNode = ensureLiveEditorHoverPopupNode(documentNode);
	if (!popupNode) {
		return;
	}

	const popupHtml = buildPopupHtml(ctx, popupText, positionHint?.payload || null);

	if (!popupHtml) {
		popupNode.style.display = 'none';
		return;
	}

	popupNode.innerHTML = popupHtml;
	popupNode.style.display = 'block';
	applyLiveEditorHoverPopupPosition(popupNode, documentNode, positionHint);
}

export function hideLiveEditorHoverPopup(documentNode) {
	const popupNode = documentNode?.getElementById?.('a11yfix-live-editor-hover-popup');
	if (!popupNode) {
		return;
	}

	popupNode.style.display = 'none';
	popupNode.innerHTML = '';
}
